import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFieldArray, useWatch, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Tab, Tabs } from 'react-bootstrap';

// Import semua dependensi versi V4
import { CreateBookingFormSchemaV4, CreateBookingFormV4 } from '../../../../services/create-booking-v4/schema/CreateBookingSchemaV4';
import { CreatedRoom, useCreateGroupBookingV4 } from '../../../../services/create-booking-v4/hooks/useCreateGroupBookingV4';

// Asumsi komponen anak juga sudah dibuat versi V4-nya
import InvoiceFormV4 from './InvoiceFormV4';
import GuestDetailFormV4 from './GuestDetailFormV4';
import CommentFormV4 from './CommentFormV4';
import PaymentSectionV4 from './PaymentSectionV4';
import ExtraSectionV4 from './ExtraSectionV4';
import { useAuth } from '../../../../auth/context/AuthProvider';
import BookingSummaryV4 from './BookingSummaryV4';
import { ShowBookingDataV4 } from '../../../../services/create-booking-v4/schema/ShowBookingResponseV4';
import { useSendInvoiceV4 } from '../../../../services/create-booking-v4/hooks/useSendInvoiceV4';
// import { IconAlertTriangle, IconCoin, IconFileInvoice, IconMail, IconMessageCircle, IconSettings, IconUser, IconX } from '@tabler/icons-react';
import { IconAlertTriangle, IconBox, IconCoin, IconFileInvoice, IconMail, IconMessageCircle, IconUser, IconX } from '@tabler/icons-react';
import SendEmailContentV4 from '../update-reservation-v4/SendEmailContentV4';
import { toast } from 'sonner';
import clsx from 'clsx';
import ErrorSummaryV4 from './ErrorSummaryV4';
import { format } from 'date-fns';
import { useDeleteBookingV4 } from '../../../../services/create-booking-v4/hooks/useDeleteBookingV4';
import ConfirmationDeleteModal from './ConfirmationDeleteModal';
import useGetProperty from '../../../property-management/hooks/useGetProperty';
import { usePropertyReadOnly } from '../../../../shared/hooks/usePropertyReadOnly';
import ReadOnlyGuard from '../../../../shared/components/ReadOnlyGuard';


// Tipe untuk state pemrosesan per kamar
export type RoomProcessingStatus = 'idle' | 'loading' | 'success' | 'error';

export interface RoomStatus {
    status: RoomProcessingStatus;
    message?: string;
}

// Tipe ini mungkin perlu didefinisikan di file terpisah jika digunakan di tempat lain
export interface RoomSelected {
    room_type_id: string;
    room_id: string | null;
    index: number;
}

interface CreateBookingPropsV4 {
    onClose: () => void;
    startDate?: string;
    endDate?: string;
    roomTypeId?: string;
    roomId?: string;
    onProcessingChange: (isProcessing: boolean) => void;
    initialData?: ShowBookingDataV4;
}

const CreateBookingComponentV4: React.FC<CreateBookingPropsV4> = ({ onClose, startDate, endDate, roomTypeId, roomId, onProcessingChange, initialData }) => {
    const { user, hasRole, hasPermission } = useAuth();
    const { isReadOnly } = usePropertyReadOnly();
    // State untuk mencegah pemilihan kamar duplikat di UI
    const [roomSelected, setRoomSelected] = useState<RoomSelected[]>([]);

    // BARU: State untuk melacak kamar yang sudah berhasil dibuat di API
    const [createdRooms, setCreatedRooms] = useState<CreatedRoom[]>([]);

    // BARU: State untuk menyimpan status setiap kamar
    const [processingStatus, setProcessingStatus] = useState<Record<number, RoomStatus>>({});

    // BARU: State untuk menyimpan info setelah sukses
    const [successfulBookingInfo, setSuccessfulBookingInfo] = useState<{ invoiceNumber: string } | null>(null);

    // BARU: State untuk mengontrol tab yang aktif
    const [activeTab, setActiveTab] = useState('invoice');
    const formRef = useRef<HTMLFormElement>(null); // BARU: Ref untuk elemen form

    // BARU: State untuk melacak apakah seluruh proses telah selesai dengan sukses
    const [isGroupBookingComplete, setGroupBookingComplete] = useState<boolean>(false);

    // Di dalam CreateBookingComponentV4.tsx, di dekat state lainnya
    const [hasDateChanged, setHasDateChanged] = useState<boolean>(false);

    // BARU: State untuk melacak total extras
    const [totalExtras, setTotalExtras] = useState<number>(0);

    // BARU: Inisialisasi hook untuk mengirim invoice
    const { mutate: sendInvoice, isPending: isSendingInvoice } = useSendInvoiceV4();

    // BARU: Ambil property_id untuk digunakan oleh useGetProperty
    const propertyId = user?.active_property_id || '';

    // BARU: Panggil hook untuk mengambil detail properti
    const { data: propertyData } = useGetProperty(propertyId);

    const { register, handleSubmit, control, setValue, reset, formState: { errors, dirtyFields, isSubmitted }, watch, setError, clearErrors } = useForm<CreateBookingFormV4>({
        resolver: zodResolver(CreateBookingFormSchemaV4),
        mode: 'onTouched',
        defaultValues: {
            // Ambil property_id dari user yang login (misalnya dari localStorage)
            property_id: user?.active_property_id || '',
            invoice_date: format(new Date(), 'yyyy-MM-dd'), // Set tanggal invoice ke hari ini
            // Default untuk data tamu dan catatan
            customer_phone: '',
            customer_address: '',
            // ...default values lainnya untuk GuestDetail dan Comments
            public_note: '',
            private_note: '',
            // Setup untuk kamar pertama
            bookingroom: [
                {
                    payment_type: 'hotel_collect',
                    customer_name: '',
                    customer_surname: '',
                    customer_email: '',
                    checkin_date: startDate || '',
                    checkout_date: endDate || '',
                    room_type_id: roomTypeId || '',
                    rate_plan_id: '',
                    room_id: roomId || null,
                    adults: 2,
                    children: 0,
                    rates: [],
                    payments: [], // Inisialisasi pembayaran sebagai array kosong
                    invoice_number: undefined, // Boleh ada (untuk update) atau tidak (untuk create)
                    status: 'confirmed',
                    mark: null,
                }
            ]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'bookingroom'
    });

    const isUpdateMode = !!initialData;

    // BARU: useEffect untuk mengisi email_autofill saat mode create
    useEffect(() => {
        // Jalankan hanya jika dalam mode 'create' dan data properti sudah tersedia
        if (!isUpdateMode && propertyData?.data?.email_autofill) {
            const emailToSet = propertyData.data.email_autofill;

            // Set nilai email untuk kamar pertama
            setValue('bookingroom.0.customer_email', emailToSet, {
                shouldValidate: true,
                shouldDirty: true
            });
        }
    }, [propertyData, isUpdateMode, setValue]);

    // Meng-handle penutupan Offcanvas
    const handleClose = useCallback(() => {
        reset();
        setCreatedRooms([]); // Reset "memori" saat ditutup
        setProcessingStatus({}); // Reset status saat ditutup
        setSuccessfulBookingInfo(null); // Reset success state saat ditutup
        setGroupBookingComplete(false); // Reset state "complete" saat ditutup
        onClose();
    }, [reset, onClose]);

    // Implementasi hook orkestrator dengan callback yang baru dan lebih detail
    const { mutate, isPending } = useCreateGroupBookingV4({
        onRoomProcessStart: (index) => {
            setProcessingStatus(prev => ({ ...prev, [index]: { status: 'loading' } }));
        },
        onRoomProcessSuccess: (result) => {
            if ('bookingId' in result) {
                setCreatedRooms(prev => [...prev, result]);
            }
            setProcessingStatus(prev => ({ ...prev, [result.index]: { status: 'success' } }));
        },
        onRoomProcessError: (index, error) => {
            // Logika untuk menentukan pesan error yang akan ditampilkan
            const status = error?.response?.status;
            let message = "An unexpected error occurred.";

            if (status === 422) {
                // Untuk error validasi, kita akan menandai fieldnya langsung
                // Pesan generik di overlay sudah cukup
                message = "Validation failed. Please check the fields.";
                const validationErrors = error?.response?.data?.errors;
                if (validationErrors) {
                    for (const [field, messages] of Object.entries(validationErrors)) {
                        // `setError` akan menangani pesan error spesifik per field
                        setError(`bookingroom.${index}.${field}` as any, {
                            type: "manual",
                            message: (messages as string[])[0],
                        });
                    }
                }
            } else if (status === 400) {
                message = error?.response?.data?.message || "There was a problem with the request.";
            } else if (status >= 500) {
                message = "A server error occurred. Please try again later.";
            } else if (!error.response) {
                message = "Network error. Please check your connection.";
            }

            setProcessingStatus(prev => ({ ...prev, [index]: { status: 'error', message: message } }));
        },
        onAllSuccess: (result) => {
            // handleClose();
            // PERBAIKAN: Jangan tutup, tapi set state sukses
            setSuccessfulBookingInfo({ invoiceNumber: result.invoiceNumber });

            // BARU: Set state "complete" menjadi true saat semua berhasil
            setGroupBookingComplete(true);
        },
        onError: (error) => {
            // Ini akan dipanggil jika ada error yang tidak ditangani per kamar
            console.error("Group booking process failed at a critical stage:", error);
        }
    });

    const onSubmit = useCallback((values: CreateBookingFormV4) => {
        if (isReadOnly) return;

        if (!hasRole('dev') && !hasPermission('booking_update')) {
            toast.error("You don't have permission to do this action");
            return;
        }

        setProcessingStatus({}); // Reset status sebelum memulai proses baru
        setGroupBookingComplete(false); // Reset state "complete" sebelum memulai proses baru
        mutate(values, { createdRooms, dirtyFields });
    }, [isReadOnly, hasRole, hasPermission, mutate, createdRooms, dirtyFields]);

    // BARU: Handler untuk tombol "Send Invoice"
    const handleSendInvoice = () => {
        if (successfulBookingInfo?.invoiceNumber) {
            sendInvoice(successfulBookingInfo.invoiceNumber);
        }
    };

    // Logika untuk menambahkan kamar baru di UI
    const handleAddRoom = () => {
        const bookingRooms = watch('bookingroom');
        if (bookingRooms.length === 0) return;

        // Ambil data tamu dari kamar pertama sebagai template
        const firstRoomData = bookingRooms[0];
        append({
            ...firstRoomData,
            // Reset data spesifik kamar
            room_type_id: '',
            rate_plan_id: '',
            room_id: null,
            adults: 2,
            children: 0,
            rates: [],
            payments: [],
            status: 'confirmed',
            mark: null,
        });
    };

    const [modalState, setModalState] = useState<{ show: boolean; indexToDelete: number | null; isLastRoom: boolean }>({
        show: false,
        indexToDelete: null,
        isLastRoom: false,
    });
    const [deleteApiError, setDeleteApiError] = useState<string | null>(null);

    const { mutate: deleteBooking, isPending: isDeletingBooking } = useDeleteBookingV4();

    const handleDeleteRoom = (index: number) => {
        if (isReadOnly) return;

        if (!hasRole('dev') && !hasPermission('booking_update')) {
            toast.error('You do not have permission to delete rooms');
            return;
        }

        if (isUpdateMode) {
            // Di mode update, izinkan delete room terakhir (akan hapus seluruh reservasi)
            const isLastRoom = fields.length <= 1;
            setDeleteApiError(null);
            setModalState({ show: true, indexToDelete: index, isLastRoom });
        } else {
            // Di mode create, tetap block penghapusan room terakhir
            if (fields.length <= 1) {
                toast.error("You cannot delete the last room.");
                return;
            }
            remove(index);
        }
    };

    const handleConfirmDelete = () => {
        if (isReadOnly) return;

        const { indexToDelete, isLastRoom } = modalState;
        if (indexToDelete === null) return;

        const roomToDelete = createdRooms.find(r => r.index === indexToDelete);
        const bookingIdToDelete = roomToDelete?.bookingId;

        if (!bookingIdToDelete) {
            setDeleteApiError("An internal error occurred: Booking ID not found.");
            return;
        }

        deleteBooking({ bookingId: bookingIdToDelete }, {
            onSuccess: () => {
                if (isLastRoom) {
                    // Last room deleted = entire reservation deleted
                    handleCloseModal();
                    handleClose(); // Close offcanvas & reset form
                } else {
                    handleCloseModal();
                    remove(indexToDelete);
                    setCreatedRooms(prev =>
                        prev
                            .filter(r => r.index !== indexToDelete)
                            .map(r => ({
                                ...r,
                                index: r.index > indexToDelete ? r.index - 1 : r.index,
                            }))
                    );
                }
            },
            onError: (error: any) => {
                const message = error?.response?.data?.message || "An unknown error occurred.";
                setDeleteApiError(message);
            }
        });
    };

    const handleCloseModal = () => {
        // Jangan izinkan tutup jika sedang proses delete
        if (isDeletingBooking) return;

        setModalState({ show: false, indexToDelete: null, isLastRoom: false });
        setDeleteApiError(null);
    };

    useEffect(() => {
        onProcessingChange(isPending);
    }, [isPending, onProcessingChange]);

    const isInitialMount = useRef(true);

    const [firstRoomCheckin, firstRoomCheckout, firstRoomPaymentType] = useWatch({
        control,
        name: [`bookingroom.0.checkin_date`, `bookingroom.0.checkout_date`, `bookingroom.0.payment_type`]
    });

    // ============== OLD Code ===============
    // // 2. Buat useEffect yang berjalan hanya jika tanggal di kamar pertama berubah
    // useEffect(() => {
    //     if (isUpdateMode) {
    //         setHasDateChanged(true);
    //     }

    //     const bookingRooms = watch('bookingroom');
    //     if (!bookingRooms || bookingRooms.length < 2) return;

    //     bookingRooms.forEach((_room, index) => {
    //         // Lewati kamar pertama
    //         if (index === 0) return;

    //         // Perbarui tanggal untuk semua kamar lainnya
    //         setValue(`bookingroom.${index}.checkin_date`, firstRoomCheckin, { shouldValidate: true });
    //         setValue(`bookingroom.${index}.checkout_date`, firstRoomCheckout, { shouldValidate: true });
    //     });
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [firstRoomCheckin, firstRoomCheckout, setValue, isUpdateMode]); // <-- Dependensi yang efisien

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // Tandai bahwa tanggal telah berubah HANYA jika ini adalah mode update
        if (isUpdateMode) {
            setHasDateChanged(true);
            // Hentikan sinkronisasi pada mode update agar tiap kamar punya tanggal mandiri
            return;
        }

        const bookingRooms = watch('bookingroom');
        if (!bookingRooms || bookingRooms.length < 2) return;

        bookingRooms.forEach((_room, index) => {
            if (index === 0) return;
            setValue(`bookingroom.${index}.checkin_date`, firstRoomCheckin, { shouldValidate: true });
            setValue(`bookingroom.${index}.checkout_date`, firstRoomCheckout, { shouldValidate: true });
            
            // MODIFIKASI: Sinkronisasi payment_type hanya dilakukan di mode Create.
            // Di mode Update, guard clause if (isUpdateMode) return di atas sudah memastikan 
            // perubahan di kamar pertama tidak menimpa kamar lainnya.
            setValue(`bookingroom.${index}.payment_type`, firstRoomPaymentType, { shouldValidate: true });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstRoomCheckin, firstRoomCheckout, firstRoomPaymentType, setValue, isUpdateMode]); // Tambahkan firstRoomPaymentType

    useEffect(() => {
        if (initialData) {
            // Transformasi data API ke format yang cocok dengan form state
            const transformedData: CreateBookingFormV4 = {
                property_id: initialData.property_id,
                invoice_date: initialData.booking[0]?.invoice_date || '',
                customer_phone: initialData.booking[0]?.customer_phone,
                customer_address: initialData.booking[0]?.customer_address,
                customer_country: initialData.booking[0]?.customer_country,
                customer_state: initialData.booking[0]?.customer_state,
                customer_city: initialData.booking[0]?.customer_city,
                customer_zip: initialData.booking[0]?.customer_zip,
                customer_date_of_birth: initialData.booking[0]?.customer_date_of_birth,
                customer_identity_number: initialData.booking[0]?.customer_identity_number,
                public_note: initialData.booking[0]?.public_note ?? undefined,
                private_note: initialData.booking[0]?.private_note ?? undefined,
                agent_id: initialData.booking[0]?.agent_id,

                // Ubah nama `booking` dari API menjadi `bookingroom` untuk form
                bookingroom: initialData.booking.map(room => ({
                    invoice_number: room.invoice_number,
                    // PERBAIKAN: Gunakan payment_type masing-masing kamar agar tetap sinkron dengan data API
                    payment_type: room.payment_type || '',
                    customer_name: room.customer_name,
                    customer_surname: room.customer_surname || '',
                    customer_email: room.customer_email || '',
                    checkin_date: room.checkin_date,
                    checkout_date: room.checkout_date,
                    room_type_id: room.room_type_id || '',
                    rate_plan_id: room.rate_plan_id,
                    room_id: room.room_id,
                    adults: room.adults,
                    children: room.children,
                    rates: room.rates.map(r => ({ date: r.date, rate: Number(r.rate) })),
                    payments: room.payments.map(p => ({
                        date_payment: p.date_payment,
                        payment_type: p.payment_type,
                        amount: Number(p.amount),
                        surcharge: Number(p.surcharge),
                    })),
                    status: room.status || '',
                    mark: room.mark || null,
                })),
            };

            // Gunakan reset untuk mengisi seluruh form dengan data yang sudah ada
            reset(transformedData);

            // Re-arm initial-mount guard SETELAH reset(). reset() mengubah nilai
            // bookingroom.0.checkin_date/checkout_date, yang membuat useWatch di bawah
            // (firstRoomCheckin/firstRoomCheckout) ikut "berubah" pada render berikutnya.
            // Tanpa re-arm ini, perubahan yang murni disebabkan reset() salah dianggap
            // sebagai user mengubah tanggal -> hasDateChanged jadi true padahal user
            // sama sekali belum menyentuh tanggal (bug: total amount ikut direkalkulasi
            // saat edit reservasi hanya untuk ubah nama/komentar).
            isInitialMount.current = true;

            // Inisialisasi state createdRooms
            const initialCreatedRooms = initialData.booking.map((room, index) => ({
                index: index,
                bookingId: room.id,
                reservationId: room.reservation_id,
            }));
            setCreatedRooms(initialCreatedRooms);

            // Seed roomSelected dari kamar yang sudah ter-assign, agar guard
            // "Room already selected" sadar sejak awal (bukan hanya setelah user mengubah pilihan).
            const initialRoomSelected = initialData.booking
                .map((room, index) => ({ index, room_type_id: room.room_type_id, room_id: room.room_id ?? null }))
                .filter((r): r is { index: number; room_type_id: string; room_id: string } => !!r.room_id);
            setRoomSelected(initialRoomSelected);
        }
    }, [initialData, reset]);

    // ====================================================================
    // LANGKAH 1: Logika untuk mendeteksi error di setiap tab
    // ====================================================================
    const hasInvoiceErrors = useMemo(() => {
        return !!errors.invoice_date || !!errors.bookingroom;
    }, [errors.invoice_date, errors.bookingroom]);

    const hasGuestDetailsErrors = useMemo(() => {
        return !!errors.customer_phone || !!errors.customer_address || !!errors.customer_country; // Tambahkan field lain jika perlu
    }, [errors.customer_phone, errors.customer_address, errors.customer_country]);

    const hasCommentsErrors = useMemo(() => {
        return !!errors.public_note || !!errors.private_note;
    }, [errors.public_note, errors.private_note]);

    const hasPaymentErrors = useMemo(() => {
        // Periksa apakah `errors.bookingroom` ada dan merupakan array
        if (!errors.bookingroom || !Array.isArray(errors.bookingroom)) {
            return false;
        }
        // Gunakan .some() untuk memeriksa apakah SETIDAKNYA SATU kamar
        // memiliki error yang terkait dengan `payments`.
        return errors.bookingroom.some(roomError => roomError && !!roomError.payments);
    }, [errors.bookingroom]);

    // ====================================================================
    // LANGKAH 2: Handler onError untuk auto-fokus dan auto-scroll
    // ====================================================================
    const onError = (errors: FieldErrors<CreateBookingFormV4>) => {
        toast.error("Please correct the errors before submitting again.");

        // Dapatkan nama field pertama yang error dari objek errors
        const firstErrorField = Object.keys(errors)[0];

        // Tentukan tab mana yang harus diaktifkan berdasarkan field error pertama
        if (firstErrorField === 'bookingroom' || firstErrorField === 'invoice_date') {
            setActiveTab('invoice');
        } else if (firstErrorField.startsWith('customer_')) {
            setActiveTab('guest_details');
        } else if (firstErrorField.startsWith('public_') || firstErrorField.startsWith('private_')) {
            setActiveTab('comments');
        } else if (firstErrorField === 'payments') { // Ini sekarang akan menjadi `bookingroom` juga
            setActiveTab('payment');
        }

        // Gunakan setTimeout untuk memastikan tab sudah berpindah sebelum kita mencoba scroll
        setTimeout(() => {
            if (formRef.current) {
                // Cari elemen input pertama yang invalid di dalam form
                const firstErrorElement = formRef.current.querySelector('.is-invalid');
                if (firstErrorElement) {
                    // Scroll ke elemen tersebut dan beri fokus
                    firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Coba beri fokus jika elemennya bisa difokuskan
                    if (typeof (firstErrorElement as HTMLElement).focus === 'function') {
                        (firstErrorElement as HTMLElement).focus({ preventScroll: true });
                    }
                }
            }
        }, 100); // Delay kecil untuk stabilitas
    };

    return (
        // <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column h-100">
        <>
            <form ref={formRef} onSubmit={handleSubmit(onSubmit, onError)} className="d-flex flex-column h-100">
                {/* Area Konten Utama yang bisa di-scroll */}
                <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
                    {isSubmitted && <ErrorSummaryV4 errors={errors} onSelectTab={setActiveTab} />}
                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k as string)} className="mb-3 text-blue" variant="underline" fill>
                        <Tab
                            eventKey='invoice'
                            title={
                                <span className={clsx("d-flex align-items-center justify-content-center", { 'text-danger': isSubmitted && hasInvoiceErrors })}>
                                    {isSubmitted && hasInvoiceErrors && <IconAlertTriangle size={14} className="me-1" />}
                                    <IconFileInvoice size={16} className="me-1" /> Invoice & Rooms
                                </span>
                            }
                        >
                            <InvoiceFormV4
                                fields={fields} // Kirim fields dari useFieldArray
                                processingStatus={processingStatus}
                                isPending={isPending} // Kirim juga status pending
                                register={register}
                                control={control}
                                errors={errors}
                                watch={watch}
                                setValue={setValue}
                                setError={setError}
                                deleteRoom={handleDeleteRoom}
                                roomSelected={roomSelected}
                                setRoomSelected={setRoomSelected}
                                clearErrors={clearErrors}
                                createdRooms={createdRooms}
                                bookingData={initialData}
                                isGroupBookingComplete={isGroupBookingComplete}
                                hasDateChanged={hasDateChanged}
                            />
                        </Tab>
                        <Tab
                            eventKey='guest_details'
                            title={
                                <span className={clsx("d-flex align-items-center justify-content-center", { 'text-danger': isSubmitted && hasGuestDetailsErrors })}>
                                    {isSubmitted && hasGuestDetailsErrors && <IconAlertTriangle size={14} className="me-1" />}
                                    <IconUser size={16} className="me-1" /> Main Contact
                                </span>
                            }
                        >
                            <GuestDetailFormV4 register={register} control={control} errors={errors} />
                        </Tab>
                        <Tab
                            eventKey='comments'
                            title={
                                <span className={clsx("d-flex align-items-center justify-content-center", { 'text-danger': isSubmitted && hasCommentsErrors })}>
                                    {isSubmitted && hasCommentsErrors && <IconAlertTriangle size={14} className="me-1" />}
                                    <IconMessageCircle size={16} className="me-1" /> Comments
                                </span>
                            }
                        >
                            <CommentFormV4 register={register} errors={errors} />
                        </Tab>
                        {isUpdateMode && initialData && (
                            <Tab
                                eventKey='send_email'
                                title={
                                    <span className="d-flex align-items-center">
                                        <IconMail size={16} className="me-1" /> Send Email
                                    </span>
                                }
                            >
                                <SendEmailContentV4 bookingData={initialData} />
                            </Tab>
                        )}
                        <Tab
                            eventKey='payment'
                            title={
                                <span className={clsx("d-flex align-items-center justify-content-center", { 'text-danger': isSubmitted && hasPaymentErrors })}>
                                    {isSubmitted && hasPaymentErrors && <IconAlertTriangle size={14} className="me-1" />}
                                    <IconCoin size={16} className="me-1" /> Payment
                                </span>
                            }
                        >
                            <PaymentSectionV4 control={control} errors={errors} fields={fields} />
                        </Tab>
                        {isUpdateMode && (
                            <Tab
                                eventKey='extra'
                                title={
                                    <span className="d-flex align-items-center justify-content-center">
                                        <IconBox size={16} className="me-1" /> Extra
                                    </span>
                                }
                            >
                                <ExtraSectionV4 createdRooms={createdRooms} onTotalExtrasChange={setTotalExtras} />
                            </Tab>
                        )}
                    </Tabs>
                </div>
                {/* Area Bawah yang Tetap (Sticky) */}
                <div className="mt-auto">
                    {/* BARU: Tampilkan komponen BookingSummaryV4 di sini */}
                    <BookingSummaryV4 control={control} totalExtras={isUpdateMode ? totalExtras : 0} />

                    {/* Tombol aksi */}
                    {/* <div className="p-3 pt-0">
                    <div className="d-flex justify-content-between gap-2">
                        <button type="button" className="btn btn-secondary" onClick={handleAddRoom}>
                            Add Another Room
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={isPending}>
                            {isPending ? 'Processing...' : 'Create Reservation'}
                        </button>
                    </div>
                </div> */}
                    <div className="p-3 pt-0">
                        {successfulBookingInfo ? (
                            // --- Tampilan Setelah Sukses ---
                            <div className="text-center">
                                <h5 className="text-success">Reservation Complete!</h5>
                                <p className="text-muted mb-3">What would you like to do next?</p>
                                <div className="d-flex justify-content-center gap-2">
                                    <Button variant="secondary" onClick={handleClose}>
                                        <IconX size={16} className="me-1" /> Finish & Close
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleSendInvoice}
                                        disabled={isSendingInvoice}
                                    >
                                        <IconMail size={16} className="me-1" />
                                        {isSendingInvoice ? 'Sending...' : 'Send Invoice'}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            // --- Tampilan Form Normal ---
                            <div className="d-flex justify-content-between gap-2">
                                <ReadOnlyGuard>
                                    <button type="button" className="btn btn-secondary" onClick={handleAddRoom}>
                                        Add Another Room
                                    </button>
                                </ReadOnlyGuard>
                                <ReadOnlyGuard>
                                    <button type="submit" className="btn btn-primary" disabled={isPending}>
                                        {isPending ? 'Processing...' : 'Create/Update Reservation'}
                                    </button>
                                </ReadOnlyGuard>
                            </div>
                        )}
                    </div>
                </div>
            </form>
            <ConfirmationDeleteModal
                show={modalState.show}
                onClose={handleCloseModal}
                onConfirm={handleConfirmDelete}
                isDeleting={isDeletingBooking}
                error={deleteApiError}
                isLastRoom={modalState.isLastRoom}
            />
        </>
    );
};

export default CreateBookingComponentV4;